import React from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { getProjectCaseStudy, IProject } from '../../../data/projects';

interface Props {
    project: IProject;
    isOpen: boolean;
    toggle: () => void;
    isDarkMode: boolean;
}

/**
 * Modal showing full project details, technologies, and gallery links.
 * @param props Modal props and selected project data.
 * @returns Project details modal element.
 */
export const ProjectDetailsModal: React.FC<Props> = ({ project, isOpen, toggle, isDarkMode }): JSX.Element => {
    const [isGalleryOpen, setIsGalleryOpen] = React.useState<boolean>(false);
    const projectStatusText: string = project.attributes.isComplete ? 'Complete' : 'In development';
    const caseStudy = getProjectCaseStudy(project);
    const hasHighlights: boolean = caseStudy.highlights.length > 0;
    const hasAdditionalDetails: boolean = !!caseStudy.additionalDetails && caseStudy.additionalDetails.length > 0;

    const hasSource: boolean = !!project.attributes.sourceCode;
    const isLive: boolean = !!project.attributes.liveDemo;
    const hasDisclaimer: boolean = project.attributes.disclaimer.trim().length > 0;
    const hasGallery: boolean = !!project.attributes.carouselImages && project.attributes.carouselImages.length > 0;

    /**
     * Creates an initial-based fallback label when a technology logo is unavailable.
     * @param title Technology title text.
     * @returns Two-character fallback text.
     */
    const getFallbackLogoText = React.useCallback((title: string): string => {
        const parts = title
            .split(/\s+/)
            .map((part) => part.trim())
            .filter(Boolean);

        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }, []);

    const gallerySlides = React.useMemo(
        () =>
            project.attributes.carouselImages && project.attributes.carouselImages.length > 0
                ? project.attributes.carouselImages.map((image) => ({
                    src: image.src,
                    alt: image.alt,
                    title: image.title,
                    description: image.alt,
                }))
                : [
                    {
                        src: project.attributes.thumbnail.src,
                        alt: project.attributes.thumbnail.alt,
                        title: project.attributes.thumbnail.title,
                        description: project.attributes.thumbnail.alt,
                    },
                ],
        [project.attributes.carouselImages, project.attributes.thumbnail.alt, project.attributes.thumbnail.src, project.attributes.thumbnail.title],
    );

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            scrollable
            size="lg"
            className={`project-details-modal ${isDarkMode ? 'project-details-modal-dark' : 'project-details-modal-light'}`}
            contentClassName="project-details-modal-shell"
        >
            <ModalHeader toggle={toggle} className="project-details-modal-header" wrapTag="div">
                <div className="project-details-modal-title-wrapper">
                    <h3 className="project-details-modal-title">{project.attributes.title}</h3>
                    <p className="project-details-modal-subtitle">
                        {project.attributes.projectType}
                        {project.attributes.yearCompleted ? ` • ${project.attributes.yearCompleted}` : ''}
                        {` • ${projectStatusText}`}
                    </p>
                </div>
            </ModalHeader>
            <ModalBody className="project-details-modal-body">
                <div className="project-details-modal-top">
                    <div className="project-details-modal-overview">
                        <div className="project-details-modal-thumbnail-wrapper">
                            <button type="button" className="project-details-modal-thumbnail-btn" onClick={() => setIsGalleryOpen(true)}>
                                <img
                                    className="project-details-modal-thumbnail"
                                    src={project.attributes.thumbnail.src}
                                    alt={project.attributes.thumbnail.alt}
                                    referrerPolicy="no-referrer"
                                    loading="lazy"
                                />
                            </button>
                        </div>

                        <div className="project-details-modal-quick-info">
                            <div className="project-details-modal-actions">
                                <div className="project-details-modal-links">
                                    {hasSource && (
                                        <a
                                            className="portfolio-btn portfolio-btn-primary project-details-modal-link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href={project.attributes.sourceCode}
                                        >
                                            <i className="fab fa-github" aria-hidden="true"></i> Source code
                                        </a>
                                    )}
                                    {isLive && (
                                        <a className="portfolio-btn portfolio-btn-primary project-details-modal-link" target="_blank" rel="noopener noreferrer" href={project.attributes.liveDemo}>
                                            <i className="fas fa-globe" aria-hidden="true"></i> Live demo
                                        </a>
                                    )}
                                    <button type="button" className="project-details-modal-gallery-link" onClick={() => setIsGalleryOpen(true)}>
                                        <i className="far fa-images" aria-hidden="true"></i> {hasGallery ? 'Open gallery' : 'Open image'}
                                    </button>
                                </div>
                            </div>

                            <div className="project-details-modal-tech">
                                <div className="project-details-modal-technologies">
                                    {project.attributes.technologies.map((tech, i) => (
                                        <div key={i} className="project-details-modal-tech-item" title={tech.title}>
                                            {tech.img.trim().length > 0 ? (
                                                <img className="project-details-modal-tech-logo" src={tech.img} alt={tech.altTag} loading="lazy" referrerPolicy="no-referrer" />
                                            ) : (
                                                <span className="project-details-modal-tech-logo-fallback" aria-label={tech.title}>
                                                    {getFallbackLogoText(tech.title)}
                                                </span>
                                            )}
                                            <span className="project-details-modal-tech-title">{tech.title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="project-details-modal-content">
                    <div className="project-details-modal-description">
                        <section className="project-details-modal-content-section" aria-label="Overview">
                            <p className="project-details-modal-section-title">Overview</p>
                            <p>{caseStudy.overview}</p>
                        </section>

                        {hasHighlights && (
                            <section className="project-details-modal-content-section" aria-label="Key engineering points">
                                <p className="project-details-modal-section-title">Key engineering points</p>
                                <ul className="project-details-modal-highlights">
                                    {caseStudy.highlights.map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {hasAdditionalDetails && (
                            <section className="project-details-modal-content-section" aria-label="Additional notes">
                                <p className="project-details-modal-section-title">Additional notes</p>
                                {caseStudy.additionalDetails?.map((item, i) => (
                                    <p key={i}>{item}</p>
                                ))}
                            </section>
                        )}
                    </div>

                    {hasDisclaimer && (
                        <p
                            className={`project-details-modal-disclaimer ${hasAdditionalDetails ? 'project-details-modal-disclaimer-footer' : ''}`}
                        >
                            {project.attributes.disclaimer}
                        </p>
                    )}
                </div>
            </ModalBody>

            <Lightbox
                plugins={[Counter, Zoom, Captions]}
                open={isGalleryOpen}
                close={() => setIsGalleryOpen(false)}
                slides={gallerySlides}
                counter={{
                    container: {
                        style: { top: 'unset', bottom: 0, left: 'unset', right: 0 },
                    },
                }}
            />
        </Modal>
    );
};

export default ProjectDetailsModal;